/**
 * Global Date Formatting Utility
 * Provides consistent date formatting across the application based on user preferences
 */

export type DateFormatType =
  | 'MM/DD/YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY-MM-DD'
  | 'DD.MM.YYYY'
  | 'DD-MM-YYYY'
  | 'MMM DD, YYYY'
  | 'DD MMM YYYY'
  | 'MMMM DD, YYYY'
  | 'DD MMMM YYYY';

export type TimeFormatType = '12h' | '24h';

export interface DateFormatSettings {
  dateFormat: DateFormatType;
  timeFormat: TimeFormatType;
  timezone?: string;
  currency?: string;
}

const DEFAULT_FORMAT: DateFormatSettings = {
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  currency: 'USD'
};

const STORAGE_KEY = 'dateFormatSettings';

export function getDateFormatSettings(): DateFormatSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_FORMAT, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading date format settings:', error);
  }
  return DEFAULT_FORMAT;
}

export function saveDateFormatSettings(settings: Partial<DateFormatSettings>): void {
  try {
    const current = getDateFormatSettings();
    const updated = { ...current, ...settings };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('dateFormatChanged', { detail: updated }));
  } catch (error) {
    console.error('Error saving date format settings:', error);
  }
}

export function formatDate(
  date: Date | string | number,
  options?: {
    includeTime?: boolean;
    format?: DateFormatType;
    timeFormat?: TimeFormatType;
  }
): string {
  try {
    // Parse ISO date strings (YYYY-MM-DD) as LOCAL dates to avoid
    // timezone shifting (new Date("2026-07-05") is UTC midnight, which
    // can shift to the previous day in negative timezones like UTC-4).
    const isISODateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
    const dateObj = isISODateOnly
      ? (([y, m, d]) => new Date(+y, +m - 1, +d))(date.split('-'))
      : typeof date === 'string' || typeof date === 'number'
        ? new Date(date)
        : date;

    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    const settings = getDateFormatSettings();
    const dateFormat = options?.format || settings.dateFormat;
    const timeFormat = options?.timeFormat || settings.timeFormat;

    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    
    const monthName = (len: 'short' | 'long') => new Intl.DateTimeFormat('en-US', { month: len }).format(dateObj);

    const pad = (n: number) => n.toString().padStart(2, '0');

    const formats: Record<string, string> = {
      'MM/DD/YYYY': `${pad(month)}/${pad(day)}/${year}`,
      'DD/MM/YYYY': `${pad(day)}/${pad(month)}/${year}`,
      'YYYY-MM-DD': `${year}-${pad(month)}-${pad(day)}`,
      'DD.MM.YYYY': `${pad(day)}.${pad(month)}.${year}`,
      'DD-MM-YYYY': `${pad(day)}-${pad(month)}-${year}`,
      'MMM DD, YYYY': `${monthName('short')} ${pad(day)}, ${year}`,
      'DD MMM YYYY': `${pad(day)} ${monthName('short')} ${year}`,
      'MMMM DD, YYYY': `${monthName('long')} ${pad(day)}, ${year}`,
      'DD MMMM YYYY': `${pad(day)} ${monthName('long')} ${year}`,
    };

    let formattedDate = formats[dateFormat] || formats['MM/DD/YYYY'];

    if (options?.includeTime) {
      formattedDate += ` ${new Intl.DateTimeFormat('en-US', {
        hour: 'numeric', minute: '2-digit',
        hour12: timeFormat === '12h',
      }).format(dateObj)}`;
    }

    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
}
